import { makeAutoObservable, runInAction } from 'mobx';
import { isAxiosError } from 'axios';
import type { CollectionPage, Product } from '@/core/domain/entities/Product';
import type { GetCollectionUseCase } from '@/core/application/use-cases/product/GetCollectionUseCase';

export class CollectionStore {
  slug = '';
  contextKey = '';
  collection: CollectionPage | null = null;
  products: Product[] = [];
  page = 0;
  isLoading = false;
  error: string | null = null;
  unavailable = false;
  retryBlocked = false;
  private generation = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly getCollection: GetCollectionUseCase) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get hasMore(): boolean {
    return Boolean(this.collection?.next);
  }

  reset(): void {
    this.generation += 1;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.slug = '';
    this.contextKey = '';
    this.collection = null;
    this.products = [];
    this.page = 0;
    this.isLoading = false;
    this.error = null;
    this.unavailable = false;
    this.retryBlocked = false;
  }

  async open(slug: string, contextKey: string): Promise<void> {
    this.reset();
    this.slug = slug;
    this.contextKey = contextKey;
    await this.loadNext();
  }

  async loadNext(): Promise<void> {
    if (!this.slug || this.isLoading || this.retryBlocked || this.unavailable || (this.page > 0 && !this.hasMore)) return;
    const generation = this.generation;
    const nextPage = this.page + 1;
    this.isLoading = true;
    this.error = null;
    try {
      const collection = await this.getCollection.execute(this.slug, nextPage);
      runInAction(() => {
        if (generation !== this.generation) return;
        const seen = new Set(this.products.map((product) => product.id));
        const additions = collection.results.filter((product) => {
          if (seen.has(product.id)) return false;
          seen.add(product.id);
          return true;
        });
        this.collection = collection;
        this.products = [...this.products, ...additions];
        this.page = nextPage;
      });
    } catch (error) {
      runInAction(() => {
        if (generation !== this.generation) return;
        const response = isAxiosError(error) ? error.response : undefined;
        if (response?.status === 404) {
          this.unavailable = true;
          this.collection = null;
          this.products = [];
        } else {
          this.error = "We couldn't load these products. Please try again.";
          const retryAfter = response?.headers?.['retry-after'];
          if (response?.status === 429 && retryAfter != null) {
            const seconds = Number(retryAfter);
            const delay = Number.isFinite(seconds) ? seconds * 1000 : Date.parse(String(retryAfter)) - Date.now();
            if (delay > 0) {
              this.retryBlocked = true;
              this.retryTimer = setTimeout(() => runInAction(() => {
                if (generation === this.generation) this.retryBlocked = false;
                this.retryTimer = null;
              }), Math.min(delay, 2147483647));
            }
          }
        }
      });
    } finally {
      runInAction(() => {
        if (generation === this.generation) this.isLoading = false;
      });
    }
  }
}
